import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import GlobalSettings from './GlobalSettings';
import Branding from './Branding';
import HallucinationsSection from './HallucinationsSection';
import { Globe, Radio, ImagePlay, EyeOff } from 'lucide-react';

const GlobalSettingsSection = ({
  globalSettings,
  handleGlobalChange,
  isDarkMode,
  timezone,
  timeFormat,
  setTimeFormat,
  reverseSort,
  setReverseSort,
  user,
  edgeServerEndpoint,
  showToast,
  handleBackupNow,
  keywords,
  newKeyword,
  setNewKeyword,
  handleAddKeyword,
  handleRemoveKeyword
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('display-language');

  // Handle URL parameter for global settings tab
  useEffect(() => {
    const globalTab = searchParams.get('globalTab');
    if (globalTab && ['display-language', 'transcription-services', 'branding', 'audio-post-processing'].includes(globalTab)) {
      setActiveTab(globalTab);
    } else {
      // Default to display-language if no tab specified
      setActiveTab('display-language');
    }
  }, [searchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: 'global', globalTab: tabId });
  };

  // Render specific section based on active tab
  const renderSection = () => (
    <GlobalSettings
      globalSettings={globalSettings}
      handleGlobalChange={handleGlobalChange}
      isDarkMode={isDarkMode}
      timezone={timezone}
      timeFormat={timeFormat}
      setTimeFormat={setTimeFormat}
      reverseSort={reverseSort}
      setReverseSort={setReverseSort}
      user={user}
      edgeServerEndpoint={edgeServerEndpoint}
      activeSection={activeTab}
    />
  );

  const containerClasses = isDarkMode
    ? 'bg-gray-900/60 border-gray-800'
    : 'bg-white border-gray-200';

  return (
    <div className={`rounded-2xl shadow-lg p-4 md:p-6 transition-colors duration-300 border ${containerClasses}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-2xl shadow-md ${isDarkMode ? 'bg-blue-600' : 'bg-blue-500'}`}>
            <Globe className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Configurations</h2>
            <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Configure system-wide defaults for language, transcription, branding, and audio processing.
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} overflow-x-auto`}>
        <nav className="flex space-x-4 md:space-x-8 min-w-max" aria-label="Tabs">
          <button
            onClick={() => handleTabChange('display-language')}
            className={`
              flex items-center gap-1 md:gap-2 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex-shrink-0
              ${activeTab === 'display-language'
                ? isDarkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-600 text-blue-600'
                : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Globe className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Display & Language</span>
            <span className="sm:hidden">Display</span>
          </button>
          <button
            onClick={() => handleTabChange('transcription-services')}
            className={`
              flex items-center gap-1 md:gap-2 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex-shrink-0
              ${activeTab === 'transcription-services'
                ? isDarkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-600 text-blue-600'
                : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Radio className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Transcription Services</span>
            <span className="sm:hidden">Transcription</span>
          </button>
          <button
            onClick={() => handleTabChange('branding')}
            className={`
              flex items-center gap-1 md:gap-2 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex-shrink-0
              ${activeTab === 'branding'
                ? isDarkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-600 text-blue-600'
                : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <ImagePlay className="w-4 h-4 md:w-5 md:h-5" />
            Branding
          </button>
          <button
            onClick={() => handleTabChange('audio-post-processing')}
            className={`
              flex items-center gap-1 md:gap-2 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex-shrink-0
              ${activeTab === 'audio-post-processing'
                ? isDarkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-600 text-blue-600'
                : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <EyeOff className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Audio Post processing</span>
            <span className="sm:hidden">Audio</span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {activeTab === 'branding' && (
          <Branding 
            isDarkMode={isDarkMode} 
            edgeServerEndpoint={edgeServerEndpoint} 
          />
        )}
        {activeTab === 'audio-post-processing' && (
          <HallucinationsSection
            keywords={keywords}
            newKeyword={newKeyword}
            setNewKeyword={setNewKeyword}
            handleAddKeyword={handleAddKeyword}
            handleRemoveKeyword={handleRemoveKeyword}
            globalSettings={globalSettings}
            handleGlobalChange={handleGlobalChange}
            isDarkMode={isDarkMode}
            edgeServerEndpoint={edgeServerEndpoint}
          />
        )}
        {activeTab !== 'branding' && activeTab !== 'audio-post-processing' && (
          renderSection()
        )}
      </div>
    </div>
  );
};

export default GlobalSettingsSection;

